import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, Buildings, Info } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

function fmtSince(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
    });
  } catch {
    return null;
  }
}

export default function ReportersPage() {
  const { user } = useAuth();
  const [reporters, setReporters] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/reporters");
        if (!stop) setReporters(data.reporters || []);
      } catch (e) {
        if (!stop) setError("Could not load the verified reporter roster.");
      }
    })();
    return () => { stop = true; };
  }, []);

  const ctaTarget = user
    ? user.verified ? null : "/apply-reporter"
    : "/signup?next=/apply-reporter";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="reporters-page">
      <header className="space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-tac-primary flex items-center gap-1.5">
          <ShieldCheck size={12} weight="fill" /> Verified Reporters
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl text-tac-text">
          The folks behind the gold shield
        </h1>
        <p className="text-[15px] text-tac-dim max-w-2xl leading-relaxed">
          These community members have been hand-verified by Firewatch admins.
          When you see their gold shield on a field update, you can trust the
          post is from a real local with eyes on the ground.
        </p>
      </header>

      <div
        className="border-l-2 border-tac-primary bg-tac-surface/40 rounded-sm px-4 py-3 flex items-start gap-2"
        data-testid="reporters-info-banner"
      >
        <Info size={14} weight="duotone" className="text-tac-primary mt-0.5 shrink-0" />
        <p className="text-[13px] text-tac-dim leading-relaxed">
          We never publish reporter email addresses. Locations and affiliations
          shown below are voluntarily supplied during application and reviewed
          before approval.
        </p>
      </div>

      {error && (
        <div
          className="text-xs text-tac-danger border border-tac-danger/40 bg-tac-danger/10 rounded-sm px-3 py-2"
          data-testid="reporters-error"
        >
          {error}
        </div>
      )}

      {reporters === null && !error ? (
        <div className="p-8 text-[15px] text-tac-dim text-center" data-testid="reporters-loading">
          Loading roster…
        </div>
      ) : reporters && reporters.length === 0 ? (
        <EmptyState />
      ) : (
        <ul
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="reporters-list"
        >
          {(reporters || []).map((r) => (
            <ReporterCard key={r.id} reporter={r} />
          ))}
        </ul>
      )}

      {ctaTarget && (
        <div
          className="mt-6 border border-tac-border bg-tac-surface/30 rounded-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="reporters-apply-cta"
        >
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-primary mb-1">
              Live near a hotspot county?
            </div>
            <p className="text-[14px] text-tac-text font-bold">
              Apply to join the verified reporter roster.
            </p>
            <p className="text-[13px] text-tac-dim mt-0.5">
              Locals with first-hand info help everyone make better evacuation
              and travel decisions.
            </p>
          </div>
          <Link
            to={ctaTarget}
            data-testid="reporters-apply-link"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-wider bg-tac-primary hover:bg-tac-primary/90 text-white rounded-sm transition-colors shrink-0"
          >
            <ShieldCheck size={12} weight="fill" /> Apply now →
          </Link>
        </div>
      )}
    </div>
  );
}

function ReporterCard({ reporter }) {
  const since = fmtSince(reporter.verified_since);
  const initial = (reporter.display_name || "?").charAt(0).toUpperCase();
  return (
    <li
      className="bg-tac-surface/40 border border-tac-border hover:border-tac-primary/60 rounded-sm p-4 transition-colors"
      data-testid={`reporter-card-${reporter.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-tac-bg border border-tac-border text-tac-primary flex items-center justify-center font-display font-black shrink-0">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className="font-bold text-[15px] text-tac-text truncate"
              data-testid={`reporter-name-${reporter.id}`}
            >
              {reporter.display_name}
            </span>
            <ShieldCheck
              size={13}
              weight="fill"
              className="text-tac-primary shrink-0"
              title="Verified Reporter"
            />
          </div>
          {reporter.location && (
            <div className="flex items-center gap-1 text-[13px] text-tac-dim mt-1">
              <MapPin size={11} weight="fill" className="shrink-0" />
              <span className="truncate">{reporter.location}</span>
            </div>
          )}
          {reporter.affiliation && (
            <div className="flex items-center gap-1 text-[12px] text-tac-faint mt-0.5">
              <Buildings size={11} weight="bold" className="shrink-0" />
              <span className="truncate">{reporter.affiliation}</span>
            </div>
          )}
          {since && (
            <div className="text-[10px] uppercase tracking-wider text-tac-faint mt-2">
              On roster since {since}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function EmptyState() {
  return (
    <div
      className="text-center py-12 border border-dashed border-tac-border rounded-sm"
      data-testid="reporters-empty"
    >
      <ShieldCheck size={28} weight="duotone" className="text-tac-primary mx-auto mb-2" />
      <p className="text-[15px] text-tac-text font-bold">No verified reporters yet</p>
      <p className="text-[13px] text-tac-dim mt-1">
        Be the first to wear the gold shield.
      </p>
    </div>
  );
}
